import { useBudgetOnboardingContext } from '@/components/cashflow/budget/BudgetOnboardingContext';
import { BudgetProgressBar } from '@/components/cashflow/budget/BudgetProgressBar';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { Pencil01 } from '@/components/common/icons';
import InputField from '@/components/common/InputField';
import { Paragraph, Text } from '@/components/common/Typography';
import { Box } from '@chakra-ui/react';
import { BudgetPurpose } from '@common/graphql/generated';
import { BUDGET_PURPOSE_COLOR_MAP } from '@common/react/budget';
import { useCurrentUser } from '@common/react/user';
import { toPercentageStr } from '@common/util/numbers';

interface BudgetAmountFormProps {
    amount: number;
    setAmount: (amount: number) => void;
    otherAmount: number;
    budgetPurpose: Exclude<BudgetPurpose, 'income'>;
}

export const BudgetAmountForm = ({
    amount,
    setAmount,
    otherAmount,
    budgetPurpose,
}: BudgetAmountFormProps) => {
    const { setCurrentStep } = useBudgetOnboardingContext();
    const { userInfo } = useCurrentUser();
    const color = BUDGET_PURPOSE_COLOR_MAP[budgetPurpose];
    const percentageOfIncome =
        amount / userInfo.combinedOrIndividualMonthlyNetIncome;

    return (
        <FlexCol>
            <FlexRow justify="center" mt={4}>
                <InputField
                    name="amount"
                    type="currency"
                    value={amount}
                    onChange={setAmount}
                    inputProps={{
                        variant: 'outline',
                        fontSize: 'lgr',
                        textAlign: 'center',
                    }}
                    inputOptions={{
                        min: 0,
                    }}
                    w={120}
                />
            </FlexRow>
            <FlexRow mt={2} mb={8} justify="center">
                {amount === 0 ? (
                    <Paragraph color="textMuted" fontSize="md">
                        {
                            {
                                fixed: 'You have no fixed expenses',
                                lifestyle: 'You have no lifestyle expenses',
                                saving_investing: 'You have no investments',
                            }[budgetPurpose]
                        }
                    </Paragraph>
                ) : (
                    <FlexRow align="flex-end" gap={2}>
                        <Text style={{ color }}>
                            {toPercentageStr(percentageOfIncome, 1)}{' '}
                            <Text color="textMuted" as="span">
                                of your monthly income
                            </Text>
                        </Text>

                        <Box
                            cursor="pointer"
                            onClick={() => setCurrentStep('income')}
                        >
                            <Pencil01 color="textMuted" size="sm" />
                        </Box>
                    </FlexRow>
                )}
            </FlexRow>

            <BudgetProgressBar
                incomeAmount={userInfo.combinedOrIndividualMonthlyNetIncome}
                currentAmount={amount}
                otherAmount={otherAmount}
                budgetPurpose={budgetPurpose}
            />
        </FlexCol>
    );
};
